<template>
	<div ref="documentRef" class="document-container">
		<!-- Page 1 : Étudiants admis -->
		<div v-if="!loading && etudiantsAdmis.length > 0" class="page">
			<!-- En-tête avec logo -->
			<div class="document-header">
				<div class="logo-container">
					<img 
						src="/images/logo-bulletin.jpg" 
						alt="Dakar Institute of Technology" 
						class="logo" 
						@load="onLogoLoad"
						@error="onLogoError"
						crossorigin="anonymous"
					/>
				</div>
			</div>

			<!-- Titre principal -->
			<h2 class="document-title">
				RESULTATS {{ sessionNom }} – {{ typeSessionLabel }}
			</h2>

			<!-- Sous-titre avec date -->
			<p class="document-subtitle">
				(Par ordre de mérite) – {{ dateDuJour }}
			</p>

			<!-- Liste des étudiants admis -->
			<div class="section">
				<h3 class="section-title">Liste des étudiants admis à la {{ typeSessionLabel }}</h3>
				<table class="results-table">
					<thead>
						<tr>
							<th>Matricule</th>
							<th>Crédits S{{ semestreNumero }}</th>
							<th>Moyenne S{{ semestreNumero }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="etudiant in etudiantsAdmis" :key="etudiant.inscription_id">
							<td>{{ etudiant.matricule }}</td>
							<td>{{ etudiant.credits_obtenus }}</td>
							<td>{{ formatMoyenne(etudiant.moyenne_generale) }}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Espace pour signature et cachet -->
			<div class="signature-space"></div>
		</div>

		<!-- Page 2 : Étudiants n'ayant pas validé le semestre (crédits insuffisants — distinct de la feuille EC rattrapage) -->
		<div v-if="!loading && etudiantsNonAdmis.length > 0" class="page page-break">
			<!-- En-tête avec logo -->
			<div class="document-header">
				<div class="logo-container">
					<img 
						src="/images/logo-bulletin.jpg" 
						alt="Dakar Institute of Technology" 
						class="logo" 
						@load="onLogoLoad"
						@error="onLogoError"
						crossorigin="anonymous"
					/>
				</div>
			</div>

			<!-- Titre principal -->
			<h2 class="document-title">
				RESULTATS {{ sessionNom }} – {{ typeSessionLabel }}
			</h2>

			<!-- Sous-titre avec date -->
			<p class="document-subtitle">
				(Par ordre de mérite) – {{ dateDuJour }}
			</p>

			<!-- Étudiants sous le seuil de crédits requis (≠ feuille de rattrapage par EC) -->
			<div class="section">
				<h3 class="section-title">{{ documentLabels.resultatsNonAdmisSectionTitle }}</h3>
				<table class="results-table">
					<thead>
						<tr>
							<th>Matricule</th>
							<th>Crédits S{{ semestreNumero }}</th>
							<th>Moyenne S{{ semestreNumero }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="etudiant in etudiantsNonAdmis" :key="etudiant.inscription_id">
							<td>{{ etudiant.matricule }}</td>
							<td>{{ etudiant.credits_obtenus }}</td>
							<td>{{ formatMoyenne(etudiant.moyenne_generale) }}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Espace pour signature et cachet -->
			<div class="signature-space"></div>
		</div>

		<!-- Message de chargement -->
		<div v-if="loading" class="loading-message">
			<p>Chargement des données...</p>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { Deliberation } from '@/types/deliberation'
import { useDeliberation } from '@/composables/useDeliberation'
import { useSessionUser } from '@/composables/useSessionUser'
import { buildMapByLaravelInscriptionId } from '@/utils/inscriptionIdHelpers'
import { fetchAllEtudiantsBisForSessionSpecialite, warnEtudiantsBisTruncated } from '@/utils/etudiantsBisPagination'
import { unwrapApiEnvelope } from '@/utils/deliberationRattrapageHelpers'
import { formatMoyenneDisplay } from '@/utils/noteMoyenneHelpers'
import {
	getDeliberationOutcomeDocumentLabels,
	getDeliberationSessionLabel,
} from '@/utils/deliberationDocumentLabels'

const props = defineProps<{
	deliberation: Deliberation
	/** `snapshotPayload.raw` — si absent, chargé automatiquement pour une délib. validée. */
	snapshotRaw?: Record<string, unknown> | null
}>()

const documentRef = ref<HTMLElement | null>(null)
const logoLoaded = ref(false)
const { getDeliberation, getDeliberationSnapshot } = useDeliberation()
const { fetchEtudiantsBisPageSilent } = useSessionUser()

type ResultatsDeliberationSource = {
	deliberation_etudiants: any[]
	deliberation_condition: { seuil_credit_requis?: number } | null
	source: 'snapshot' | 'live'
}

async function resolveDeliberationSource(): Promise<ResultatsDeliberationSource | null> {
	const useSnapshot = props.deliberation.etat === 'valide'
	let raw = props.snapshotRaw ?? null

	if (useSnapshot && !raw) {
		const r = await getDeliberationSnapshot(props.deliberation.id)
		if (r.success && r.data?.raw) {
			raw = r.data.raw as Record<string, unknown>
		}
	}

	if (raw) {
		const delibEtudiants = unwrapApiEnvelope<any[]>(raw.deliberation_etudiants_get)
		const deliberationMeta = unwrapApiEnvelope<Record<string, unknown>>(raw.deliberation_get)
		if (Array.isArray(delibEtudiants)) {
			const condition =
				(deliberationMeta?.deliberation_condition as ResultatsDeliberationSource['deliberation_condition']) ??
				null
			return {
				deliberation_etudiants: delibEtudiants,
				deliberation_condition: condition,
				source: 'snapshot',
			}
		}
	}

	const result = await getDeliberation(props.deliberation.id)
	if (!result.success || !result.data) return null

	return {
		deliberation_etudiants: result.data.deliberation_etudiants ?? [],
		deliberation_condition: result.data.deliberation_condition ?? null,
		source: 'live',
	}
}

const etudiantsAdmis = ref<any[]>([])
/** Étudiants sous le seuil de crédits — pas la liste EC de `etudiants-rattrapage`. */
const etudiantsNonAdmis = ref<any[]>([])
const loading = ref(true)

function onLogoLoad() {
	logoLoaded.value = true
}

function onLogoError() {
	console.warn('Logo failed to load, continuing without it')
	logoLoaded.value = true // Continue anyway
}

const sessionNom = computed(() => {
	const numero = props.deliberation.semestre?.numero || ''
	const specialite = props.deliberation.specialite?.intitule || ''
	const sessionCode = props.deliberation.session?.code || props.deliberation.session?.nom || ''
	
	// Formater comme dans le document : "SEMESTRE 1 MASTER2_Jan25"
	if (specialite && numero) {
		// Extraire le niveau (M1, M2, etc.) depuis la spécialité
		const niveauMatch = specialite.match(/(M[12]|L[123])/i)
		const niveau = niveauMatch ? niveauMatch[0].toUpperCase() : ''
		return `SEMESTRE ${numero} ${niveau}${sessionCode ? '_' + sessionCode : ''}`
	}
	return `SEMESTRE ${numero}${sessionCode ? ' ' + sessionCode : ''}`
})

const semestreNumero = computed(() => {
	return props.deliberation.semestre?.numero || ''
})

const typeSessionLabel = computed(() => getDeliberationSessionLabel(props.deliberation.type_deliberation))

const documentLabels = computed(() =>
	getDeliberationOutcomeDocumentLabels(props.deliberation.type_deliberation)
)

const dateDuJour = computed(() => {
	return new Date().toLocaleDateString('fr-FR', { 
		day: '2-digit', 
		month: '2-digit', 
		year: 'numeric' 
	})
})

function formatMoyenne(moyenne: number | null | undefined): string {
	if (moyenne === null || moyenne === undefined) return '-'
	return formatMoyenneDisplay(moyenne)
}

async function loadEtudiants() {
	loading.value = true
	try {
		const deliberationSource = await resolveDeliberationSource()
		if (!deliberationSource) {
			console.error('Erreur lors du chargement de la délibération (résultats session)')
			return
		}

		// Charger tous les étudiants de la session/spécialité pour récupérer les matricules
		const { results: allStudents, truncated } = await fetchAllEtudiantsBisForSessionSpecialite(
			fetchEtudiantsBisPageSilent,
			props.deliberation.session_id,
			props.deliberation.specialite_id
		)
		warnEtudiantsBisTruncated(truncated, 'résultats session')

		const matriculeMap = buildMapByLaravelInscriptionId(allStudents, (student) => {
			const matricule = student.matricule || student.code || null
			return matricule ? String(matricule).trim() : ''
		})

		const delibEtudiants = deliberationSource.deliberation_etudiants
		if (Array.isArray(delibEtudiants)) {
			const listeFormatee: any[] = []

			for (const delibEtudiant of delibEtudiants) {
				const inscriptionId = Number(delibEtudiant.inscription_id)
				
				// Récupérer le matricule depuis la map ou depuis l'inscription
				let matricule = matriculeMap.get(inscriptionId)
				
				if (!matricule) {
					// Essayer depuis l'inscription dans delibEtudiant
					matricule = delibEtudiant.inscription?.etudiant?.matricule
						|| delibEtudiant.inscription?.matricule
						|| delibEtudiant.inscription?.etudiant?.code
						|| ''
					matricule = String(matricule).trim()
				}

				// Si toujours pas de matricule, utiliser l'ID comme fallback
				if (!matricule) {
					matricule = `ETU-${inscriptionId}`
				}

				const creditsObtenus = delibEtudiant.credits_obtenus !== null && delibEtudiant.credits_obtenus !== undefined
					? Number(delibEtudiant.credits_obtenus)
					: 0

				const moyenneGenerale = delibEtudiant.moyenne_generale !== null && delibEtudiant.moyenne_generale !== undefined
					? Number(delibEtudiant.moyenne_generale)
					: 0

				const etudiant = {
					inscription_id: inscriptionId,
					matricule: matricule,
					credits_obtenus: creditsObtenus,
					moyenne_generale: moyenneGenerale
				}

				listeFormatee.push(etudiant)
			}

			// Séparer admis (crédits ≥ seuil) et non admis (crédits insuffisants).
			// Distinct de la feuille de rattrapage (EC via GET /etudiants-rattrapage).
			const seuilCredits =
				Number(deliberationSource.deliberation_condition?.seuil_credit_requis) ||
				(delibEtudiants[0]?.credits_requis != null
					? Number(delibEtudiants[0].credits_requis)
					: 30) ||
				30

			etudiantsAdmis.value = listeFormatee
				.filter(e => e.credits_obtenus >= seuilCredits)
				.sort((a, b) => b.moyenne_generale - a.moyenne_generale)

			etudiantsNonAdmis.value = listeFormatee
				.filter(e => e.credits_obtenus < seuilCredits)
				.sort((a, b) => b.credits_obtenus - a.credits_obtenus)
		}

	} catch (error) {
		console.error('Erreur lors du chargement des étudiants:', error)
	} finally {
		loading.value = false
	}
}

onMounted(async () => {
	await loadEtudiants()
})

// Exposer une méthode pour vérifier si les données sont chargées
defineExpose({
	documentRef,
	isReady: computed(() => !loading.value && logoLoaded.value)
})
</script>

<style scoped>
.document-container {
	width: 210mm;
	background: white;
	font-family: 'Times New Roman', Times, serif;
	box-sizing: border-box;
	color: #000;
}

.page {
	width: 210mm;
	padding: 20mm;
	box-sizing: border-box;
	display: block;
	overflow: visible;
}

.page-break {
	margin-top: 0;
}

.document-header {
	text-align: center;
	margin-bottom: 15mm;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.logo-container {
	background-color: transparent;
	padding: 0;
	border-radius: 0;
	display: inline-block;
	margin: 0 auto 5mm auto;
	text-align: center;
}

.logo {
	max-width: 200px;
	width: 200px;
	height: auto;
	display: block;
	object-fit: contain;
}

.school-name {
	font-size: 14px;
	font-weight: bold;
	margin: 0;
	margin-bottom: 3mm;
	color: #000;
	text-transform: uppercase;
}

.school-tagline {
	font-size: 11px;
	margin: 0;
	color: #000;
	font-style: italic;
}

.document-title {
	text-align: center;
	font-size: 14px;
	font-weight: bold;
	margin-bottom: 6mm;
	text-transform: uppercase;
	color: #000;
}

.document-subtitle {
	text-align: center;
	font-size: 11px;
	margin-bottom: 12mm;
	color: #000;
}

.section {
	margin-bottom: 20mm;
}

.section-title {
	font-size: 14px;
	font-weight: bold;
	margin-bottom: 6mm;
	text-align: center;
	color: #000;
}

.results-table {
	width: 100%;
	border-collapse: collapse;
	margin-bottom: 12mm;
	font-size: 13px;
	border: 1px solid #000;
}

.results-table thead {
	background-color: #002c40;
	color: white;
}

.results-table th {
	border: 1px solid #000;
	padding: 6px 8px;
	text-align: center;
	font-weight: bold;
	font-size: 13px;
	color: white;
	background-color: #002c40;
}

.results-table td {
	border: 1px solid #000;
	padding: 5px 8px;
	text-align: center;
	font-size: 13px;
	color: #000;
}

.results-table tbody tr:nth-child(even) {
	background-color: #fff;
}

.loading-message {
	text-align: center;
	padding: 20mm 0;
	color: #666;
	font-style: italic;
}

.signature-space {
	margin-top: 20mm;
	height: 40mm;
}
</style>
